import { browserAuthHeaders } from './browserAuthHeaders'
import i18n from '@/i18n'
import { normalizeLocale, defaultLocale } from '@/config/locales'
import { getAccessToken, getTenantId } from '@/services/tokenStorage'

/**
 * Streams one copilot chat turn from POST /api/ai/chat. The backend
 * responds with the Vercel AI SDK data protocol (SSE `data:` lines);
 * this fetch-based reader exists because EventSource cannot send the
 * Passport bearer token.
 */
export interface AiChatStreamCallbacks {
  onTextDelta: (delta: string) => void
  onApprovalRequest: (approval: { toolCallId: string; reason?: string | null }) => void
  onDone: () => void
  onError: (message: string) => void
  // Optional: when provided, a user-initiated cancellation (AbortError) is
  // surfaced here instead of onDone(), letting the caller distinguish a
  // cancelled turn from a successful one. When omitted, cancellation falls
  // back to onDone() so existing consumers keep finalizing the message.
  onAbort?: () => void
}

export interface AiChatStreamOptions extends AiChatStreamCallbacks {
  signal?: AbortSignal
  lang?: string
}

interface VercelProtocolPart {
  type?: string
  delta?: string
  toolCallId?: string
  approvalId?: string
  reason?: string | null
  errorMessage?: string
}

function authHeaders(lang?: string): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'text/event-stream',
    'X-Requested-With': 'XMLHttpRequest',
  }

  const token = getAccessToken()
  Object.assign(headers, browserAuthHeaders(token))

  const tenantId = getTenantId()
  if (tenantId) headers['X-Tenant-ID'] = tenantId

  headers['Accept-Language'] = normalizeLocale(lang ?? i18n.language) ?? defaultLocale

  return headers
}

export async function streamAiChat(
  message: string,
  conversationId: string | null,
  options: AiChatStreamOptions,
): Promise<void> {
  const { onTextDelta, onApprovalRequest, onDone, onError, onAbort, signal, lang } = options

  try {
    const response = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: authHeaders(lang),
      credentials: 'include',
      signal,
      body: JSON.stringify({ message, conversation_id: conversationId, lang }),
    })

    if (!response.ok || !response.body) {
      let messageText = `Request failed (${response.status})`
      try {
        const payload = await response.json()
        if (payload?.message) messageText = payload.message
      } catch {
        // non-JSON error body
      }
      onError(messageText)
      return
    }

    // The backend always answers this route as an SSE stream
    // (StreamableAgentResponse::usingVercelProtocol sends
    // `Content-Type: text/event-stream`). A 200 that is not a stream means a
    // proxy/login page answered instead, and reading it as `data:` frames would
    // hang the drawer with no error at all.
    const contentType = response.headers.get('content-type') ?? ''
    if (!contentType.includes('text/event-stream')) {
      onError(`Unexpected response type (${response.status})`)
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    for (;;) {
      const { done, value } = await reader.read()
      if (done) break

      buffer += decoder.decode(value, { stream: true })
      // SSE fields terminate with CRLF or a bare LF; splitting on '\n' only
      // leaves a trailing '\r' that breaks the `data:` prefix match on some
      // proxies/servers.
      const lines = buffer.split(/\r?\n/)
      buffer = lines.pop() ?? ''

      for (const line of lines) {
        const trimmed = line.trim()
        if (!trimmed.startsWith('data:')) continue

        const payload = trimmed.slice(5).trim()
        if (payload === '' || payload === '[DONE]') continue

        let part: VercelProtocolPart
        try {
          part = JSON.parse(payload)
        } catch {
          continue
        }

        if (part.type === 'text-delta' && typeof part.delta === 'string') {
          onTextDelta(part.delta)
        } else if (part.type === 'tool-approval-request') {
          // Without an id the approval cannot be acted on, so skip it rather
          // than handing consumers an empty identifier.
          const toolCallId = part.toolCallId ?? part.approvalId
          if (toolCallId) {
            onApprovalRequest({ toolCallId, reason: part.reason })
          }
        } else if (part.type === 'error') {
          onError(part.errorMessage ?? 'The AI request failed.')
          // A protocol error is terminal: stop reading and DO NOT fall through
          // to onDone(), so consumers never receive both an error and a done
          // for the same turn.
          return
        }
      }
    }

    onDone()
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') {
      // Cancellation is surfaced via onAbort when the caller provides it, so
      // a cancelled turn is distinguishable from a successful one; otherwise
      // fall back to onDone() to preserve the finalization contract existing
      // consumers rely on.
      if (onAbort) {
        onAbort()
      } else {
        onDone()
      }
      return
    }
    onError((error as Error)?.message ?? 'Network error while streaming.')
  }
}
